import type { Point2D, GeoCoord, Viewport } from './mapGeometry';
import {
  metricToCanvas,
  calculateZoomLevel,
  localMetersToGeo,
  projectToLocalMeters,
  getTileCoords,
  tileToLat,
  tileToLng,
} from './mapGeometry';

export type SatelliteSource = 'GOOGLE' | 'VECTOR';

export interface TileRendererOptions {
  source: SatelliteSource;
  contrastOverlay?: boolean;
}

/**
 * Draws satellite imagery tiles or an offline vector grid directly onto the canvas context.
 */
export function renderMapBackground(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  width: number,
  height: number,
  anchor: GeoCoord,
  source: SatelliteSource,
  tileCache: Map<string, HTMLImageElement>,
  requestRedraw: () => void,
  contrastOverlay = true
) {
  const { center, scale } = viewport;
  const toCanvas = (pt: Point2D) => metricToCanvas(pt, center, scale, width, height);

  if (source === 'VECTOR') {
    // Offline Vector Grid
    ctx.strokeStyle = '#17171f';
    ctx.lineWidth = 1;
    const gridSizeMeters = scale > 1 ? 50 : scale > 0.1 ? 500 : 2000;
    const startGridX = Math.floor((center.x - width / 2 / scale) / gridSizeMeters) * gridSizeMeters;
    const endGridX = Math.ceil((center.x + width / 2 / scale) / gridSizeMeters) * gridSizeMeters;
    const startGridY = Math.floor((center.y - height / 2 / scale) / gridSizeMeters) * gridSizeMeters;
    const endGridY = Math.ceil((center.y + height / 2 / scale) / gridSizeMeters) * gridSizeMeters;

    ctx.beginPath();
    for (let gx = startGridX; gx <= endGridX; gx += gridSizeMeters) {
      const p1 = toCanvas({ x: gx, y: startGridY });
      const p2 = toCanvas({ x: gx, y: endGridY });
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
    }
    for (let gy = startGridY; gy <= endGridY; gy += gridSizeMeters) {
      const p1 = toCanvas({ x: startGridX, y: gy });
      const p2 = toCanvas({ x: endGridX, y: gy });
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
    }
    ctx.stroke();
    return;
  }

  // Satellite Imagery Tiles
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  let zoom = calculateZoomLevel(scale, anchor.lat);
  if (zoom > 20) {
    zoom = 20;
  }

  // Visible metric bounds
  const visMinX = center.x - width / 2 / scale;
  const visMaxX = center.x + width / 2 / scale;
  const visMinY = center.y - height / 2 / scale;
  const visMaxY = center.y + height / 2 / scale;

  // Visible corners in WGS84
  const nwGeo = localMetersToGeo({ x: visMinX, y: visMaxY }, anchor);
  const seGeo = localMetersToGeo({ x: visMaxX, y: visMinY }, anchor);

  const tileMin = getTileCoords(nwGeo.lat, nwGeo.lng, zoom);
  const maxTile = getTileCoords(seGeo.lat, seGeo.lng, zoom);

  const startX = Math.min(tileMin.x, maxTile.x) - 1;
  const endX = Math.max(tileMin.x, maxTile.x) + 1;
  const startY = Math.min(tileMin.y, maxTile.y) - 1;
  const endY = Math.max(tileMin.y, maxTile.y) + 1;

  const totalTiles = (endX - startX + 1) * (endY - startY + 1);
  if (totalTiles <= 80) {
    for (let tileX = startX; tileX <= endX; tileX++) {
      for (let tileY = startY; tileY <= endY; tileY++) {
        const tileKey = `${source}_${zoom}_${tileX}_${tileY}`;

        const nwLat = tileToLat(tileY, zoom);
        const nwLng = tileToLng(tileX, zoom);
        const seLat = tileToLat(tileY + 1, zoom);
        const seLng = tileToLng(tileX + 1, zoom);

        const pNW = projectToLocalMeters({ lat: nwLat, lng: nwLng }, anchor);
        const pSE = projectToLocalMeters({ lat: seLat, lng: seLng }, anchor);

        const cNW = toCanvas(pNW);
        const cSE = toCanvas(pSE);

        const tileWidth = cSE.x - cNW.x;
        const tileHeight = cSE.y - cNW.y;

        let img = tileCache.get(tileKey);
        if (!img) {
          img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = `https://mt1.google.com/vt/lyrs=s&x=${tileX}&y=${tileY}&z=${zoom}`;
          img.onload = () => requestRedraw();
          tileCache.set(tileKey, img);
        } else if (img.complete && img.naturalWidth > 0) {
          ctx.drawImage(img, cNW.x, cNW.y, tileWidth, tileHeight);
        }
      }
    }
  }

  if (contrastOverlay) {
    // Translucent dark contrast overlay for track visibility
    ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
    ctx.fillRect(0, 0, width, height);
  }
}
